import React from 'react';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import {
  getOrderTipoPreventivaCode,
  getOsTipoChangeOptions,
  getServiceTypeName,
} from '../utils/orderDetailsModalUtils';

export function useOrderDetailsTipoOsEdit(order, { isAdmin = false, onTipoUpdated } = {}) {
  const [tipoLabel, setTipoLabel] = React.useState('');
  const [tipoCode, setTipoCode] = React.useState('');
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [feedback, setFeedback] = React.useState(null);
  const menuRef = React.useRef(null);

  React.useEffect(() => {
    const code = getOrderTipoPreventivaCode(order);
    setTipoCode(code);
    setTipoLabel(getServiceTypeName(code));
    setMenuOpen(false);
    setFeedback(null);
  }, [order?.id]);

  React.useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  const changeOptions = React.useMemo(
    () => (isAdmin ? getOsTipoChangeOptions(tipoCode) : []),
    [isAdmin, tipoCode]
  );

  const handleSelectTipo = React.useCallback(
    async (novoTipo) => {
      if (!isAdmin || !order?.id || isSaving) return;
      const code = String(novoTipo ?? '')
        .trim()
        .toUpperCase();
      if (!code || code === tipoCode) {
        setMenuOpen(false);
        return;
      }
      const token = localStorage.getItem('authToken');
      if (!token) return;
      setIsSaving(true);
      setFeedback(null);
      try {
        const res = await fetch(`${getApiBaseUrl()}/api/orders/${order.id}/tipo-os`, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ novoTipo: code }),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok || !json.success) {
          throw new Error(json.message || 'Erro ao alterar tipo da OS');
        }
        const label = getServiceTypeName(code);
        setTipoCode(code);
        setTipoLabel(label);
        onTipoUpdated?.(code, label);
        setFeedback({ type: 'success', message: 'Tipo de OS atualizado.' });
        setMenuOpen(false);
        setTimeout(() => setFeedback(null), 2500);
      } catch (err) {
        setFeedback({ type: 'error', message: err.message || 'Erro ao alterar tipo da OS' });
      } finally {
        setIsSaving(false);
      }
    },
    [isAdmin, order?.id, isSaving, tipoCode, onTipoUpdated]
  );

  return {
    tipoLabel,
    tipoCode,
    menuOpen,
    setMenuOpen,
    isSaving,
    feedback,
    menuRef,
    changeOptions,
    canEditTipo: isAdmin && changeOptions.length > 0,
    handleSelectTipo,
  };
}
